import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../SafeIcon';
import { FaSpotify, FaApple, FaYoutube } from 'react-icons/fa';

const { FiMusic, FiX, FiPlay, FiPause } = FiIcons;

const AudioPlayer = ({ url, accentColor, textColor }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const current = audioRef.current.currentTime;
    
    if (current >= 30) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    } else {
      setProgress((current / 30) * 100);
      setCurrentTime(current);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-4 w-full max-w-sm mb-6">
      <button 
        onClick={togglePlay}
        className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-full text-white shadow-xl transition-all hover:scale-105 active:scale-95"
        style={{ backgroundColor: accentColor }}
      >
        <SafeIcon icon={isPlaying ? FiPause : FiPlay} className={`w-6 h-6 ${isPlaying ? '' : 'ml-1'}`} style={{ fill: 'currentColor' }} />
      </button>
      
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex justify-between text-xs font-bold mb-2 tracking-wider" style={{ color: textColor, opacity: 0.6 }}>
          <span>{formatTime(currentTime)}</span>
          <span>0:30</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden relative group cursor-pointer">
          <div className="absolute inset-0" style={{ backgroundColor: textColor, opacity: 0.2 }} />
          <div 
            className="h-full absolute top-0 left-0 transition-all duration-100 ease-linear" 
            style={{ width: `${progress}%`, backgroundColor: accentColor }}
          />
        </div>
      </div>

      <audio 
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); setProgress(0); setCurrentTime(0); }}
      />
    </div>
  );
};

const ArtistWidget = ({ styles = {}, onConfigure }) => {
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [embedUrl, setEmbedUrl] = useState('');

  const {
    tracks = [],
    showArtistName = true,
    artistName = 'Artist Name',
    cardBgColor = '#ffffff',
    textColor = '#111827',
    accentColor = '#5494d4',
    borderRadius = 16,
  } = styles;

  const platforms = [
    { id: 'spotify', name: 'Spotify', icon: FaSpotify, color: '#1DB954' },
    { id: 'apple', name: 'Apple Music', icon: FaApple, color: '#FA2D48' },
    { id: 'youtube', name: 'YouTube', icon: FaYoutube, color: '#FF0000' }
  ];

  const handlePlatformClick = (track, platformId) => {
    const link = track.links?.[platformId];
    if (!link) return;
    
    let genEmbedUrl = '';

    if (platformId === 'spotify') {
      const trackId = link.split('/track/')[1]?.split('?')[0];
      if (trackId) genEmbedUrl = `https://open.spotify.com/embed/track/${trackId}`;
    } else if (platformId === 'youtube') {
      let videoId = '';
      if (link.includes('youtube.com/watch?v=')) {
        videoId = link.split('watch?v=')[1]?.split('&')[0];
      } else if (link.includes('youtu.be/')) {
        videoId = link.split('youtu.be/')[1]?.split('?')[0];
      }
      if (videoId) genEmbedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    } else if (platformId === 'apple') {
      if (link.includes('music.apple.com')) {
        genEmbedUrl = link.replace('music.apple.com', 'embed.music.apple.com');
      }
    }

    if (genEmbedUrl) {
      setEmbedUrl(genEmbedUrl);
      setSelectedTrack(track);
      setSelectedPlatform(platformId);
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const closePlayer = () => {
    setSelectedTrack(null);
    setSelectedPlatform(null);
    setEmbedUrl('');
  };

  if (tracks.length === 0 && !onConfigure) {
    return (
      <div className="w-full border-2 border-dashed border-purple-300 bg-purple-50/50 rounded-2xl p-10 flex flex-col items-center justify-center">
        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
          <SafeIcon icon={FiMusic} className="w-6 h-6 text-purple-500" />
        </div>
        <p className="font-black text-purple-600 text-sm uppercase tracking-widest">Artist Widget</p>
        <p className="text-xs text-purple-400 mt-1">Click to add your music tracks</p>
      </div>
    );
  }

  return (
    <div className="w-full py-6 relative">
      <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
        {showArtistName && artistName && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-2" style={{ color: textColor }}>
              {artistName}
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <SafeIcon icon={FiMusic} className="w-4 h-4" style={{ color: accentColor }} />
              <span className="text-sm font-bold" style={{ color: textColor, opacity: 0.6 }}>
                {tracks.length} Track{tracks.length !== 1 ? 's' : ''} Available
              </span>
            </div>
          </div>
        )}
        
        <div className="space-y-8">
          {tracks.map((track, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="flex flex-col md:flex-row p-6 md:p-10 shadow-2xl transition-all duration-300 relative group overflow-hidden"
              style={{ 
                backgroundColor: cardBgColor,
                borderRadius: `${borderRadius}px`,
                color: textColor
              }}
            >
              <div 
                className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay"
                style={{
                  background: `linear-gradient(135deg, ${accentColor}40 0%, transparent 100%)`
                }}
              />

              <div className="flex-shrink-0 w-full md:w-64 mb-8 md:mb-0 md:mr-10 relative z-10">
                <div className="aspect-square w-full rounded-xl shadow-2xl overflow-hidden flex items-center justify-center relative group-hover:scale-[1.02] transition-transform duration-500" style={{ backgroundColor: `${textColor}10` }}>
                  {track.coverUrl ? (
                    <img 
                      src={track.coverUrl} 
                      alt={track.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <SafeIcon icon={FiMusic} className="w-16 h-16 opacity-20" style={{ color: textColor }} />
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center relative z-10">
                <div className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ opacity: 0.6 }}>
                  Track
                </div>
                
                <h3 className="font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter mb-4 leading-tight" style={{ color: textColor }}>
                  {track.title}
                </h3>
                
                {track.artist && (
                  <div className="flex items-center gap-3 mb-6 font-bold text-lg" style={{ opacity: 0.9 }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center relative overflow-hidden">
                      <div className="absolute inset-0" style={{ backgroundColor: textColor, opacity: 0.15 }} />
                      <SafeIcon icon={FiMusic} className="w-4 h-4 relative z-10" />
                    </div>
                    {track.artist}
                  </div>
                )}
                
                {track.description && (
                  <p className="text-sm md:text-base leading-relaxed mb-6 max-w-2xl" style={{ opacity: 0.7 }}>
                    {track.description}
                  </p>
                )}

                {track.audioUrl && (
                  <AudioPlayer url={track.audioUrl} accentColor={accentColor} textColor={textColor} />
                )}

                <div className="flex flex-wrap gap-3 mt-2">
                  {platforms.map((platform) => {
                    const hasLink = track.links?.[platform.id];
                    if (!hasLink) return null;
                    
                    return (
                      <motion.button
                        key={platform.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handlePlatformClick(track, platform.id)}
                        className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm text-white shadow-lg hover:shadow-xl transition-all"
                        style={{ backgroundColor: platform.color }}
                      >
                        <SafeIcon icon={platform.icon} className="w-4 h-4" />
                        <span>{platform.name}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Music Player Modal */}
      <AnimatePresence>
        {selectedTrack && selectedPlatform && embedUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
            onClick={closePlayer}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center space-x-4">
                  {selectedTrack.coverUrl && (
                    <img 
                      src={selectedTrack.coverUrl} 
                      alt={selectedTrack.title}
                      className="w-16 h-16 rounded-xl object-cover shadow-lg"
                    />
                  )}
                  <div>
                    <h3 className="font-black text-xl text-gray-900">
                      {selectedTrack.title}
                    </h3>
                    {selectedTrack.artist && (
                      <p className="text-sm text-gray-600 font-medium">
                        {selectedTrack.artist}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={closePlayer}
                  className="p-2 hover:bg-white rounded-full transition-colors text-gray-500 hover:text-gray-700"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    {platforms.find(p => p.id === selectedPlatform) && (
                      <>
                        <SafeIcon 
                          icon={platforms.find(p => p.id === selectedPlatform).icon} 
                          className="w-5 h-5"
                          style={{ color: platforms.find(p => p.id === selectedPlatform).color }}
                        />
                        <span className="font-bold text-gray-700">
                          Playing on {platforms.find(p => p.id === selectedPlatform).name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex-1 bg-black rounded-2xl overflow-hidden">
                  {selectedPlatform === 'spotify' && (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="w-full h-full min-h-[352px]"
                    />
                  )}
                  
                  {selectedPlatform === 'youtube' && (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full min-h-[400px]"
                    />
                  )}
                  
                  {selectedPlatform === 'apple' && (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay *; encrypted-media *; fullscreen *"
                      sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                      className="w-full h-full min-h-[450px]"
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtistWidget;